import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { ApplicationStateService } from './application-state.service';
import { CommunicationService } from './communication.service';

@Injectable({
    providedIn: 'root'
})
export class BookFeatureActivationService {

    /**
     * Tells if the user wants to see the specific element on the page.
     * Changes in this should not effect the UI.
     */
    rectsVisible = true;
    textSelectionsVisible = true;
    audiosVisible = true;
    videosVisible = true;
    interactiveTasksVisible = true;
    linksVisible = true;
    pageDrawingsVisible = true;
    readspeakerElementsVisible = true;

    constructor(private applicationStateService: ApplicationStateService,
                private communicationService: CommunicationService) {
    }

    /**
     * Tells if the feature is activated.
     * This also influences the appearance of UI elements (e.g. if the Notes can be created)
     */
    private _readspeakerActivated: boolean = environment.features.readspeakerActivated;

    get readspeakerActivated(): boolean {
        return this._readspeakerActivated;
    }

    set readspeakerActivated(value: boolean) {
        this._readspeakerActivated = value;
        this.communicationService.toggleReadspeakerVisibility();

    }

    private _rectsActivated: boolean = environment.features.rectsActivated;

    get rectsActivated(): boolean {
        return this._rectsActivated;
    }

    set rectsActivated(value: boolean) {
        this._rectsActivated = value;
    }

    private _textSelectionActivated: boolean = environment.features.textSelectionActivated;

    get textSelectionActivated(): boolean {
        return this._textSelectionActivated;
    }

    set textSelectionActivated(value: boolean) {
        this._textSelectionActivated = value;
    }

    private _schoolBagActivated: boolean = environment.features.schoolBagActivated;

    get schoolBagActivated(): boolean {
        return this._schoolBagActivated;
    }

    set schoolBagActivated(value: boolean) {
        this._schoolBagActivated = value;
    }

    private _exportPagesActivated: boolean = environment.features.exportPagesActivated;

    get exportPagesActivated(): boolean {
        return this._exportPagesActivated;
    }

    set exportPagesActivated(value: boolean) {
        this._exportPagesActivated = value;
    }

    private _quickLinksActivated: boolean = environment.features.quickLinksActivated;

    get quickLinksActivated(): boolean {
        return this._quickLinksActivated;
    }

    set quickLinksActivated(value: boolean) {
        this._quickLinksActivated = value;
    }

    private _audiosActivated: boolean = environment.features.audiosActivated;

    get audiosActivated(): boolean {
        return this._audiosActivated;
    }

    set audiosActivated(value: boolean) {
        this._audiosActivated = value;
    }

    private _videosActivated: boolean = environment.features.videosActivated;

    get videosActivated(): boolean {
        return this._videosActivated;
    }

    set videosActivated(value: boolean) {
        this._videosActivated = value;
    }

    private _interactiveTasksActivated: boolean = environment.features.interactiveTasksActivated;

    get interactiveTasksActivated(): boolean {
        return this._interactiveTasksActivated;
    }

    set interactiveTasksActivated(value: boolean) {
        this._interactiveTasksActivated = value;
    }

    private _linksActivated: boolean = environment.features.linksActivated;

    get linksActivated(): boolean {
        return this._linksActivated;
    }

    set linksActivated(value: boolean) {
        this._linksActivated = value;
    }

    private _bookAdditionalsActivated: boolean = environment.features.bookAdditionalsActivated;

    get bookAdditionalsActivated(): boolean {
        return this._bookAdditionalsActivated;
    }

    set bookAdditionalsActivated(value: boolean) {
        this._bookAdditionalsActivated = value;
    }

    private _bookFavoritesActivated: boolean = environment.features.bookFavoritesActivated;

    get bookFavoritesActivated(): boolean {
        return this._bookFavoritesActivated;
    }

    set bookFavoritesActivated(value: boolean) {
        this._bookFavoritesActivated = value;
    }

    private _pageDrawingActivated: boolean = environment.features.pageDrawingsActivated;

    get pageDrawingActivated(): boolean {
        return this._pageDrawingActivated;
    }

    set pageDrawingActivated(value: boolean) {
        this._pageDrawingActivated = value;
    }

    /**
     * Initialize this service.
     */
    init(): void {
        this.updateFeatureAvailability();
    }

    /**
     * Updates the availability of the features.
     */
    private updateFeatureAvailability(): void {
        if (this.applicationStateService.isOfflineModeEnabled) {
            this.readspeakerActivated = false;
            this.rectsActivated = false;
            this.schoolBagActivated = false;
            this.exportPagesActivated = false;
            this.quickLinksActivated = true;
            this.audiosActivated = false;
            this.videosActivated = false;
            this.interactiveTasksActivated = false;
            this.linksActivated = false;
            this.bookAdditionalsActivated = false;
            this.bookFavoritesActivated = false;
            this.pageDrawingActivated = false;
        }

        if (this.applicationStateService.ownerId) {
            this.exportPagesActivated = false;
            this.schoolBagActivated = false;
        }

        if (this.applicationStateService.userBookId
            || !this.applicationStateService.userData.loggedIn
            || !this.applicationStateService.userData.accessTo.copyToSchoolbag
        ) {
            this.schoolBagActivated = false;
        }

        if (!this.applicationStateService.bookConfig.bookPermissions.pageExport.view) {
            this.exportPagesActivated = false;
        }

        if (!this.applicationStateService.bookConfig.bookPermissions.quickLinks.view) {
            this.quickLinksActivated = false;
        }

        if (!this.applicationStateService.bookConfig.readSpeakerEnabled) {
            this.readspeakerActivated = false;
        }

        this.readspeakerElementsVisible = false;
    }

}
